import { Decimal } from 'decimal.js';

export interface ExplainedLeg {
  book: string;
  odds: string;
  stake: string;
  back: string;
}

export interface ExplainedFork {
  legs: [ExplainedLeg, ExplainedLeg];
  staked: string;
  back: string;
  profit: string;
  share: number;
}

function parseMoney(raw: string): Decimal | null {
  const text = raw.replace(/\s/g, '').replace(',', '.').replace(/[₽$¢]/g, '');
  if (!text) return null;
  try {
    const value = new Decimal(text);
    if (!value.isFinite() || value.lte(0)) return null;
    return value;
  } catch {
    return null;
  }
}

function money(value: Decimal): string {
  const fixed = value.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toFixed(2);
  const [whole, frac] = fixed.split('.');
  const body = (whole ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009');
  return frac === '00' ? `${body} $` : `${body}.${frac} $`;
}

/** Худшая выплата минус поставленное. Те же числа, что на карточке. */
export function explainFork(
  legs: { book: string; odds: string; stake?: string }[],
): ExplainedFork | null {
  if (legs.length < 2) return null;
  const pair = legs.slice(0, 2).map((leg) => {
    const odds = parseMoney(leg.odds);
    const stake = parseMoney(leg.stake ?? '');
    if (odds === null || odds.lte(1) || stake === null) return null;
    return { book: leg.book, odds, oddsText: leg.odds, stake };
  });
  const [left, right] = pair;
  if (left == null || right == null) return null;
  const backLeft = left.stake.times(left.odds);
  const backRight = right.stake.times(right.odds);
  const staked = left.stake.plus(right.stake);
  const worst = backLeft.lt(backRight) ? backLeft : backRight;
  const profit = worst.minus(staked);
  const share = left.stake.div(staked).times(100).toDecimalPlaces(2).toNumber();
  return {
    legs: [
      { book: left.book, odds: left.oddsText, stake: money(left.stake), back: money(backLeft) },
      { book: right.book, odds: right.oddsText, stake: money(right.stake), back: money(backRight) },
    ],
    staked: money(staked),
    back: money(worst),
    profit: money(profit.abs()),
    share,
  };
}
